import { ModeSpec, ModeSpecOptions } from 'codemirror';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Controlled as CodeMirror } from 'react-codemirror2';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { ButtonSquare } from '@xsolla/ui-core';
import { Checkmark, Copy, EyeClose, EyeOpen } from '@xsolla/ui-icons';

import { useFirstRender } from '../../../utils';
import { CopyButtonWrapper, CopyWrapper, Label, LabelWrapper, Wrapper } from './codeBlockStyles';
import { Wrapper as CodeWrapper } from './styles';

/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-ignore
import('codemirror/lib/codemirror.css');
// @ts-ignore
import('codemirror/theme/material.css');
// @ts-ignore
import('codemirror/mode/htmlmixed/htmlmixed.js');

type Props = {
  disabled?: boolean;
  initialValue?: string;
  hideDataValue?: string;
  label?: string;
  extra?: ReactNode;
  allowCopyCode?: boolean;
  height?: number;
  mode?: string | ModeSpec<ModeSpecOptions>;
};

const CodeBlock = ({
  disabled = false,
  initialValue = '',
  hideDataValue = '',
  label = '',
  extra,
  allowCopyCode = false,
  height = 634,
  mode = 'htmlmixed'
}: Props): JSX.Element => {
  const [copied, setCopied] = useState<boolean>(false);
  const [hideValues, setHideValues] = useState<boolean>(!!hideDataValue);
  const firstRender = useFirstRender();

  useEffect(() => {
    if (!firstRender && copied) {
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [copied]);

  const currentValue = useMemo(() => {
    return hideValues ? hideDataValue : initialValue;
  }, [hideValues]);

  const renderCopyButton = () => (
    <CopyWrapper data-test-id="copy-button">
      <CopyButtonWrapper>
        {hideDataValue && (
          <ButtonSquare
            type="button"
            palette="brand"
            onClick={() => setHideValues((prevState) => !prevState)}>
            {hideValues ? <EyeOpen /> : <EyeClose />}
          </ButtonSquare>
        )}
        <CopyToClipboard text={initialValue}>
          <ButtonSquare palette="brand" disabled={copied} onClick={() => setCopied(true)}>
            {copied ? <Checkmark /> : <Copy />}
          </ButtonSquare>
        </CopyToClipboard>
      </CopyButtonWrapper>
    </CopyWrapper>
  );

  return (
    <Wrapper>
      <LabelWrapper extra={!!extra}>
        <Label>{label}</Label>

        {extra && extra}
      </LabelWrapper>
      <CodeWrapper height={height} data-test-id="code-preview">
        <CodeMirror
          className="code-preview-wrapper"
          value={currentValue}
          options={{
            readOnly: disabled,
            mode,
            theme: 'material',
            spellcheck: false
          }}
          onBeforeChange={() => null}
        />

        {allowCopyCode && renderCopyButton()}
      </CodeWrapper>
    </Wrapper>
  );
};

export default CodeBlock;
